import React, { Context } from 'react';
import Socket from '../../connection/socket';
import useShotsCustomHook, { ShotDirection } from '../../hooks/use-shots';
import { GraphicInterface } from '../../stores/reducers/graphics';
import { Profile } from '../../stores/reducers/invitations';
import { MediaItem } from '../../stores/reducers/media';
import { baseShot, Shot, ShotFeed } from '../../stores/reducers/shots';
import { DragItem, DropCallbackData } from '../../stores/reducers/user-interface';

export interface ShotsContextInterface {
	legacyHandleSelectShot: {
		(
			newCurrentShot: Shot,
			socket: Socket | null,
			blockSyncOnFirstLoad?: boolean,
		): Shot;
	};
	handleSelectShot: {
		(
			newCurrentShot: number,
			forceWrite?: boolean
		): void;
	};
	legacySetSharedMediaForShot: {
		(newCurrentShot: Shot, socket: Socket | null): void;
	};
	feedDroppedOnCanvas: {
		(
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			feedPosition: number,
			audioOnly?: boolean,
		): Promise<null>;
	};
	removeFeedFromShot: {
		(
			deleteItem: ShotFeed,
			socket: Socket,
			isForShotEditorModal: boolean,
		): Promise<null>;
	};
	overlayDroppedOnCanvas: {
		(
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			dropAreaData: DropCallbackData,
		): Promise<null>;
	};
	titleDroppedOnCanvas: {
		(
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			dropData: DropCallbackData
		): Promise<null>;
	};
	stripFeedFromShots: {
		(
			presenter: number | null,
			email: string | null,
			media: number | null,
			screenshare: string | null,
			isForShotEditorModal?: boolean,
		): void;
	};
	editShotModalShot: {
		(newShotModalShot: Shot, socket: Socket | null): void;
	};
	handleAddingShot: {
		(
			newShotData: Shot
		): Promise<Shot | null>;
	};
	changeShotTitle: { (newTitle: string, shotId: number): void };
	changeShotDuration: {
		(
			newDuration: number,
			shotBeingEdited: Shot,
			durationRef: React.MutableRefObject<HTMLInputElement | null>,
			isForShotEditorModal: boolean,
			socket: Socket | null,
		): void;
	};
	playShot: { (shotId: number, socket: Socket): void };
	getMissingFeeds: { (shotFeeds: ShotFeed[]): string };
	getShotHasMediaVideo: {
		(selectedShot: Shot, currentMediaArray: MediaItem[]): boolean;
	};
	hiddenTwoUpUserProfile: { (shotFeeds: ShotFeed[]): Profile | null };
	isShotBuilderOpen: boolean;
	currentShot: Shot;
	nextShot: Shot | undefined;
	globalShotGraphics: number[];
	combinedShotGraphics: number[];
	combinedShotGraphicsIsMax: boolean;
	otherShotGraphicsIsMax: boolean;
	idsOfVideosInCanvas: string[];
	isOnCanvas: boolean;
	isNextShot: boolean;
	setNextShotValue: (value: boolean) => void;
	currentShotGraphics: GraphicInterface[];
	currentShotAutoGraphicIds: number[];
	shotDirection: ShotDirection;
	getShotDuration: (mediaId: number, oldDuration?: number) => Promise<number>;
	lockAutoPlay: boolean;
	checkMaxOverlays: (isForShotEditorModal: boolean) => boolean;
}

export const defaultContext = {
	legacyHandleSelectShot: () => {},
	handleSelectShot: () => {},
	legacySetSharedMediaForShot: () => {},
	feedDroppedOnCanvas: () => {},
	removeFeedFromShot: () => {},
	overlayDroppedOnCanvas: () => {},
	stripFeedFromShots: () => {},
	editShotModalShot: () => {},
	handleAddingShot: () => {},
	changeShotTitle: () => {},
	changeShotDuration: () => {},
	playShot: () => {},
	getMissingFeeds: () => '',
	getShotHasMediaVideo: () => false,
	hiddenTwoUpUserProfile: () => null,
	isShotBuilderOpen: false,
	currentShot: baseShot,
	globalShotGraphics: [],
	combinedShotGraphics: [],
	combinedShotGraphicsIsMax: false,
	otherShotGraphicsIsMax: false,
	idsOfVideosInCanvas: [],
	isOnCanvas: false,
	isNextShot: false,
	setNextShotValue: () => {},
	currentShotGraphics: [],
	currentShotAutoGraphicIds: [],
	shotDirection: 'right',
	getShotDuration: () => Promise.resolve(0),
	lockAutoPlay: false,
	checkMaxOverlays: () => false,
};

export const ShotsContext = React.createContext(
	defaultContext,
) as unknown as Context<ShotsContextInterface>;

export const ShotsProvider = (props: any) => {
	const useShotsHook = useShotsCustomHook();
	return (
		<ShotsContext.Provider value={useShotsHook}>
			{props.children}
		</ShotsContext.Provider>
	);
};

export default function useShots() { return React.useContext(ShotsContext); }
